/** Modal для импорта CSV. Принимает файл, отправляет multipart, показывает report. */

import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Loader2, Upload, FileText, CheckCircle2, AlertCircle } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { api } from "@/lib/api";

interface Props {
  open: boolean;
  onClose: () => void;
}

interface ImportReport {
  created: number;
  updated: number;
  errors: { row: number; message: string }[];
}

export function CsvImportDialog({ open, onClose }: Props): JSX.Element {
  const queryClient = useQueryClient();
  const [file, setFile] = useState<File | null>(null);
  const [report, setReport] = useState<ImportReport | null>(null);

  const upload = useMutation({
    mutationFn: async () => {
      if (!file) throw new Error("Выберите файл");
      const fd = new FormData();
      fd.append("file", file);
      fd.append("lang", "ru");
      const { data } = await api.post("/products/csv-import", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      return data as ImportReport;
    },
    onSuccess: (r) => {
      setReport(r);
      toast.success(`Импорт: создано ${r.created}, обновлено ${r.updated}`);
      queryClient.invalidateQueries({ queryKey: ["admin-products"] });
    },
    onError: (e: any) => toast.error(e?.message ?? "Ошибка импорта"),
  });

  const reset = () => {
    setFile(null);
    setReport(null);
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && handleClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Импорт товаров из CSV</DialogTitle>
          <DialogDescription>
            UTF-8 .csv с заголовками: <code>sku, base_price, name</code> и т.д.
            Существующие товары обновятся по id или SKU; остальные создадутся.
          </DialogDescription>
        </DialogHeader>

        {!report && (
          <div className="space-y-3">
            <label className="flex flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed border-border p-8 cursor-pointer hover:bg-muted/50 transition-colors">
              <Upload className="w-8 h-8 text-muted-foreground" />
              <span className="text-sm font-medium">
                {file ? file.name : "Выберите CSV-файл"}
              </span>
              {file && (
                <span className="text-xs text-muted-foreground">
                  {(file.size / 1024).toFixed(1)} KB
                </span>
              )}
              <input
                type="file"
                accept=".csv,text/csv"
                onChange={(e) => setFile(e.target.files?.[0] ?? null)}
                className="hidden"
              />
            </label>

            <p className="text-xs text-muted-foreground">
              Сначала скачайте шаблон через «Экспорт CSV» — у него правильные
              колонки. Цены в тийинах (1 сум = 100 тийинов).
            </p>
          </div>
        )}

        {report && (
          <div className="space-y-3">
            <div className="flex items-center gap-3 rounded-lg bg-success/10 p-3 text-success">
              <CheckCircle2 className="w-5 h-5 shrink-0" />
              <div className="text-sm">
                <div className="font-medium">Импорт завершён</div>
                <div className="text-xs">
                  Создано: <strong>{report.created}</strong>, обновлено:{" "}
                  <strong>{report.updated}</strong>
                </div>
              </div>
            </div>

            {report.errors.length > 0 && (
              <div className="rounded-lg bg-destructive/10 p-3">
                <div className="flex items-center gap-2 mb-2 text-destructive">
                  <AlertCircle className="w-4 h-4" />
                  <span className="text-sm font-medium">
                    Ошибок: {report.errors.length}
                  </span>
                </div>
                <ul className="max-h-40 overflow-auto text-xs space-y-1 text-destructive/80">
                  {report.errors.slice(0, 20).map((e, i) => (
                    <li key={i}>
                      <strong>Строка {e.row}:</strong> {e.message}
                    </li>
                  ))}
                  {report.errors.length > 20 && (
                    <li className="font-medium">
                      … и ещё {report.errors.length - 20}
                    </li>
                  )}
                </ul>
              </div>
            )}
          </div>
        )}

        <DialogFooter>
          {!report ? (
            <>
              <Button variant="outline" onClick={handleClose}>
                Отмена
              </Button>
              <Button
                onClick={() => upload.mutate()}
                disabled={!file || upload.isPending}
              >
                {upload.isPending && (
                  <Loader2 className="w-4 h-4 animate-spin" />
                )}
                <FileText className="w-4 h-4" />
                Загрузить
              </Button>
            </>
          ) : (
            <Button onClick={handleClose}>Готово</Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
